(() => {
  const PRODUCT_HANDLE = window.limencyPillowPreviewSettings?.productHandle;
  const PHOTO_FIELD_TOKEN = 'pet-photo';
  const ROOT_CLASS = 'bonaper-pillow-upload-first';
  const READY_CLASS = 'bonaper-pillow-upload-first--ready';
  const HIDDEN_ATTR = 'data-bonaper-pillow-gated';
  const THUMB_WIDTH = 360;

  if (!document.documentElement.classList.contains(ROOT_CLASS)) return;

  const productInfo = document.querySelector('[id^="ProductInfo-"]');
  if (!productInfo) return;

  const gatedNodes = new Set();
  let observer = null;
  let ready = false;
  let thumbJob = 0;

  const commerceSelectors = [
    '.product-price-block',
    'variant-selects',
    'variant-radios',
    '[id^="Quantity-Form-"]',
    'product-form.product-form',
    '.shopify-payment-button',
    '[data-bonaper-actions]',
    '[data-bonaper-sticky]',
  ];

  function gate(node) {
    if (!(node instanceof HTMLElement) || gatedNodes.has(node)) return;
    gatedNodes.add(node);
    node.setAttribute(HIDDEN_ATTR, '');
    node.setAttribute('aria-hidden', 'true');
  }

  function release(node) {
    if (!(node instanceof HTMLElement)) return;
    node.removeAttribute(HIDDEN_ATTR);
    node.removeAttribute('aria-hidden');
  }

  function gateCommerce() {
    for (const selector of commerceSelectors) {
      productInfo.querySelectorAll(selector).forEach(gate);
    }
  }

  function getPhotoField() {
    return productInfo.querySelector(`.bonaper-field[data-bonaper-field="${PHOTO_FIELD_TOKEN}"]`);
  }

  function arrangeFields() {
    const photoField = getPhotoField();
    if (!photoField) return false;

    const fieldsHost = photoField.parentElement;
    if (fieldsHost && fieldsHost.firstElementChild !== photoField) {
      fieldsHost.prepend(photoField);
    }

    const existingIntro = photoField.querySelector('.bonaper-pillow-gate__intro');
    if (!existingIntro) {
      const intro = document.createElement('div');
      intro.className = 'bonaper-pillow-gate__intro';
      intro.innerHTML = '<strong>Upload your pet photo first</strong><span>We will create your custom pillow preview before you choose a size.</span>';
      photoField.prepend(intro);
    }

    if (!ready && fieldsHost) {
      fieldsHost.querySelectorAll('.bonaper-field').forEach((field) => {
        if (field !== photoField) gate(field);
      });
    }

    return true;
  }

  // Fallback when the BonaPer hook is unavailable. After the photo editor's
  // Apply, BonaPer shows a data: snapshot instead of the processed asset URL.
  function processedPhotoReady() {
    const photoField = getPhotoField();
    if (!photoField) return false;

    const failed = photoField.querySelector('.bonaper-field__note--ai:not([hidden])');
    if (failed) return false;

    const thumb = photoField.querySelector('.bonaper-photo__thumb:not([hidden])');
    if (!(thumb instanceof HTMLImageElement) || !thumb.src) return false;
    if (thumb.dataset.bonaperPillowThumb === 'processed') return true;
    if (thumb.src.startsWith('data:') || thumb.src.startsWith('blob:')) return true;

    const normalized = decodeURIComponent(thumb.src).toLowerCase();
    return normalized.includes('processed/') || normalized.includes('/processed');
  }

  function popularSizeInput() {
    const popular = productInfo.querySelector('[data-pillow-size-popular]');
    const id = popular?.closest('label')?.getAttribute('for');
    return id ? document.getElementById(id) : null;
  }

  function selectPopularSize() {
    if (new URLSearchParams(location.search).has('variant')) return;
    const input = popularSizeInput();
    if (input instanceof HTMLInputElement && !input.checked && !input.classList.contains('disabled')) {
      input.click();
    }
  }

  function unlock() {
    if (ready) return;
    ready = true;
    document.documentElement.classList.add(READY_CLASS);
    gatedNodes.forEach(release);
    gatedNodes.clear();

    const photoField = getPhotoField();
    const intro = photoField && photoField.querySelector('.bonaper-pillow-gate__intro');
    if (intro) intro.remove();

    if (photoField && !photoField.querySelector('.bonaper-pillow-gate__ready')) {
      const note = document.createElement('p');
      note.className = 'bonaper-pillow-gate__ready';
      note.setAttribute('role', 'status');
      note.textContent = 'Your pillow preview is ready. Choose a size below.';
      photoField.append(note);
    }

    selectPopularSize();

    window.dispatchEvent(new CustomEvent('bonaper:pillow-ready', {
      detail: { productHandle: PRODUCT_HANDLE },
    }));
  }

  function sync() {
    if (ready) return;
    gateCommerce();
    arrangeFields();
    if (processedPhotoReady()) unlock();
  }

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    });
  }

  // BonaPer shows an editor snapshot after Apply but the uncropped asset after
  // a draft resume. Render both cases the same way: the cutout inside the crop.
  async function renderThumb(thumb, subject, value) {
    const job = ++thumbJob;
    try {
      const image = await loadImage(subject.url);
      if (job !== thumbJob) return;
      const crop = value.crop || { x: 0, y: 0, w: image.naturalWidth, h: image.naturalHeight };
      const canvas = document.createElement('canvas');
      canvas.width = THUMB_WIDTH;
      canvas.height = Math.round(THUMB_WIDTH * crop.h / crop.w);
      const ctx = canvas.getContext('2d');
      const angle = Number(value.rotation || 0) * Math.PI / 180;
      const scale = canvas.width / crop.w;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(angle);
      ctx.scale(scale, scale);
      ctx.translate(-(crop.x + crop.w / 2), -(crop.y + crop.h / 2));
      ctx.drawImage(image, 0, 0);
      const processed = subject.ai || String(value.assetToken || '').startsWith('processed/');
      thumb.dataset.bonaperPillowThumb = processed ? 'processed' : 'original';
      thumb.src = canvas.toDataURL('image/png');
    } catch {
      // Keep BonaPer's own thumbnail when the asset cannot be read.
    }
  }

  function onPhotoCommitted(block) {
    const session = block.photoSessions?.get(PHOTO_FIELD_TOKEN);
    const value = block.state?.[PHOTO_FIELD_TOKEN];
    const control = block.controls?.get(PHOTO_FIELD_TOKEN);
    if (!session || !value || typeof value !== 'object') return;

    const subject = block.photoSubject?.(session);
    const thumb = control?.photo?.thumb;
    if (subject?.url && thumb instanceof HTMLImageElement) renderThumb(thumb, subject, value);

    // Edit photo → Apply commits again after the form is already open; gating
    // then would hide price, size and Add to cart with nothing to release them.
    if (!ready && String(value.assetToken || '').startsWith('processed/')) {
      gateCommerce();
      arrangeFields();
      unlock();
    }
  }

  async function hookBonaPer() {
    const script = [...document.scripts].find((item) => /\/bonaper\.js(?:\?|$)/.test(item.src) && item.src.includes('/extensions/'));
    if (!script) return;
    try {
      const module = await import(script.src);
      const proto = (module.BonaPerBlock || module.m)?.prototype;
      if (!proto || proto.__bonaperPillowGateHook) return;
      proto.__bonaperPillowGateHook = true;

      const commitPhoto = proto.commitPhoto;
      if (typeof commitPhoto === 'function') {
        proto.commitPhoto = function (...args) {
          const result = commitPhoto.apply(this, args);
          if (args[0]?.token === PHOTO_FIELD_TOKEN) onPhotoCommitted(this);
          return result;
        };
      }

      const finishPhotoAdoption = proto.finishPhotoAdoption;
      if (typeof finishPhotoAdoption === 'function') {
        proto.finishPhotoAdoption = function (...args) {
          const result = finishPhotoAdoption.apply(this, args);
          onPhotoCommitted(this);
          return result;
        };
      }
    } catch {
      // The thumbnail fallback in sync() still unlocks the form.
    }
  }

  hookBonaPer();
  gateCommerce();
  sync();

  observer = new MutationObserver(sync);
  observer.observe(productInfo, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['hidden', 'src', 'class'],
  });

})();
